import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';

const clarityBaseUrl =
  // @ts-expect-error
  import.meta.env.VITE_APP_CLARITY_URL || 'http://127.0.0.1:8787';

const chrome = globalThis.chrome;

// Custom baseQuery that detects environment and uses appropriate method
const createClarityBaseQuery = (): BaseQueryFn => {
  // Check if we're in a Chrome extension context
  const isExtension =
    typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.id;

  if (isExtension) {
    // Chrome extension implementation using message passing
    return async (args, api, extraOptions) => {
      // Handle case where args is just a string (URL)
      let url, method, body, params, headers;

      if (typeof args === 'string') {
        url = args;
        method = 'GET';
        headers = {};
        params = undefined;
        body = undefined;
      } else {
        // Normal object args
        ({ url, method, body, params, headers = {} } = args);
      }

      if (url.includes('undefined')) {
        console.error('Clarity API URL contains undefined:', url);
        return {
          error: {
            status: 'CUSTOM_ERROR',
            data: 'URL contains undefined. Please check the request parameters.',
          },
        };
      }

      // Special handling for FormData with files
      if (body instanceof FormData) {
        // Check if FormData contains any File objects
        let hasFiles = false;
        for (const [_, value] of body.entries()) {
          if (value instanceof File) {
            hasFiles = true;
            break;
          }
        }

        if (hasFiles) {
          // For FormData with files, we need to handle the fetch directly
          // in the content script since we can't pass File objects via messaging
          try {
            // Get auth token from background script
            const tokenResponse = await new Promise<{ token: string | null }>(
              (resolve) => {
                chrome.runtime.sendMessage({ type: 'GET_AUTH_TOKEN' }, resolve);
              }
            );

            // Create a headers object first
            const headersObj: Record<string, string> = {
              'Content-Type': 'application/json',
              ...headers,
            };

            // Add authorization if token exists
            if (tokenResponse && tokenResponse.token) {
              headersObj['Authorization'] = `Bearer ${tokenResponse.token}`;
            }

            const fetchOptions: RequestInit = {
              method: method || 'GET',
              body,
              headers: headersObj,
            };

            // Do the fetch directly from content script
            const response = await fetch(clarityBaseUrl + url, fetchOptions);

            if (!response.ok) {
              const errorData = await response.json();
              return { error: { status: response.status, data: errorData } };
            }

            const data = await response.json();
            return { data };
          } catch (error: any) {
            console.error('Error in clarity API content script fetch:', error);
            return {
              error: {
                status: 'FETCH_ERROR',
                data: error.message || 'Failed to fetch',
              },
            };
          }
        }
      }

      // For non-FormData requests or FormData without files
      const isFormData = body instanceof FormData;

      // Process FormData for messaging
      let processedBody = body;
      if (isFormData) {
        const formDataObj: Record<string, string | Blob> = {};
        for (const [key, value] of body.entries()) {
          formDataObj[key] = value;
        }
        processedBody = formDataObj;
      }

      return new Promise((resolve) => {
        chrome.runtime.sendMessage(
          'ekpmbeilnaahlccpgjchjjoonecikehf', // Your extension ID
          {
            type: 'API_REQUEST',
            endpoint: clarityBaseUrl + url,
            method: method || 'GET',
            body: processedBody,
            params,
            headers: {
              'Content-Type': 'application/json',
              ...headers,
            },
            isFormData, // Flag to indicate this is FormData
          },
          (response) => {
            // Check for runtime errors first
            if (chrome.runtime.lastError) {
              console.error(
                'Chrome runtime error (clarity):',
                chrome.runtime.lastError
              );
              resolve({
                error: {
                  status: 'RUNTIME_ERROR',
                  data: chrome.runtime.lastError.message,
                },
              });
              return;
            }

            // Handle no response
            if (!response) {
              console.error('No response from extension (clarity)');
              resolve({
                error: {
                  status: 'NO_RESPONSE',
                  data: 'No response from extension',
                },
              });
              return;
            }

            // Handle response
            if (response.error) {
              resolve({
                error: { status: 'CUSTOM_ERROR', data: response.error },
              });
            } else {
              resolve({ data: response.data });
            }
          }
        );
      });
    };
  } else {
    // Regular web app implementation using fetchBaseQuery
    return fetchBaseQuery({
      baseUrl: clarityBaseUrl,
      prepareHeaders: (headers) => {
        headers.set('Content-Type', 'application/json');
        return headers;
      },
      timeout: 300000,
    });
  }
};

// Use the appropriate baseQuery based on environment
const clarityFetchBase = createClarityBaseQuery();

export interface EnhancedElementProperties {
  tagName?: string;
  className?: string;
  id?: string;
  textContent?: string;
  computedStyles?: Record<string, string> | CSSStyleDeclaration;
  position?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  contextualInfo?: {
    semanticType: string;
    purpose: string;
    interactionType: string;
    pageSection: string | null;
    businessContext: string;
    contentCategory: string;
    nearbyElements: string[];
    userAction: string | null;
    sizeCategory: string;
    viewportPosition: string;
  };
}

export interface ClarityAnalysisRequest {
  commentId: string;
  feedback: string;
  elementProperties?: EnhancedElementProperties;
  cacheKey?: string;
  screenshot?: string;
  elementScreenshot?: string;
  userName?: string;
}

export interface ClarificationOption {
  id: number;
  text: string;
}

export interface ClarityAnalysisResponse {
  score: number;
  reply: string;
  options?: ClarificationOption[];
  cached?: boolean;
  needsClarification: boolean;
}

export interface ClarificationGenerationRequest {
  commentId: string;
  selectedOptionIds: number[];
  originalFeedback: string;
  options: ClarificationOption[];
  screenshot?: string;
  elementScreenshot?: string;
  userName?: string;
}

export interface ClarificationGenerationResponse {
  clarificationText: string;
  success: boolean;
}

export interface BrandGuidelinesResponse {
  summary: Summary
  success: boolean
}

export interface Summary {
  brandName: string
  projectTypes: string[]
  targetAudience: string
  toneOfVoice: string
  additionalContext: string
}

export const clarityApi = createApi({
  reducerPath: 'clarityApi',
  baseQuery: clarityFetchBase,
  tagTypes: ['ClarityAnalysis', 'CommentClarification'],
  endpoints: (builder) => ({
    analyzeFeedbackClarity: builder.mutation<
      ClarityAnalysisResponse,
      ClarityAnalysisRequest
    >({
      query: (data) => ({
        url: '/analyze-clarity',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { commentId }) => {
        if (error) return [];
        return [
          { type: 'ClarityAnalysis', id: commentId },
          { type: 'CommentClarification', id: commentId },
        ];
      },
    }),
    generateClarification: builder.mutation<
      ClarificationGenerationResponse,
      ClarificationGenerationRequest
    >({
      query: (data) => ({
        url: '/generate-clarification',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { commentId }) => {
        if (error) return [];
        return [{ type: 'CommentClarification', id: commentId }];
      },
    }),
    generateBrandGuidelines: builder.mutation<
      BrandGuidelinesResponse,
      string
    >({
      query: (url) => ({
        url: '/generate-brand-guidelines',
        method: 'POST',
        body: { url },
      }),
    }),
  }),
});

export const {
  useAnalyzeFeedbackClarityMutation,
  useGenerateClarificationMutation,
  useGenerateBrandGuidelinesMutation,
} = clarityApi;

// Helper hooks for common patterns
export const useClarityWorkflow = () => {
  const [
    analyzeClarity,
    { data: analysisData, isLoading: isAnalyzing, error: analysisError },
  ] = useAnalyzeFeedbackClarityMutation();

  const [
    generateClarification,
    {
      data: clarificationData,
      isLoading: isGenerating,
      error: clarificationError,
    },
  ] = useGenerateClarificationMutation();

  const startClarityAnalysis = async (request: ClarityAnalysisRequest) => {
    try {
      const result = await analyzeClarity(request).unwrap();
      return result;
    } catch (error) {
      console.error('Clarity analysis failed:', error);
      throw error;
    }
  };

  const completeClarification = async (
    request: ClarificationGenerationRequest
  ) => {
    try {
      const result = await generateClarification(request).unwrap();
      return result;
    } catch (error) {
      console.error('Clarification generation failed:', error);
      throw error;
    }
  };

  return {
    // Actions
    startClarityAnalysis,
    completeClarification,

    // Analysis state
    analysisData,
    isAnalyzing,
    analysisError,

    // Clarification state
    clarificationData,
    isGenerating,
    clarificationError,

    // Combined state
    isLoading: isAnalyzing || isGenerating,
    hasError: !!analysisError || !!clarificationError,
  };
};

// Utility functions for working with clarity data
export const clarityUtils = {
  needsClarification: (score: number): boolean => score > 0.5,

  getClarityLevel: (
    score: number
  ): 'specific' | 'mostly-specific' | 'moderate' | 'vague' | 'very-vague' => {
    if (score <= 0.2) return 'specific';
    if (score <= 0.4) return 'mostly-specific';
    if (score <= 0.6) return 'moderate';
    if (score <= 0.8) return 'vague';
    return 'very-vague';
  },

  getClarityDescription: (score: number): string => {
    const level = clarityUtils.getClarityLevel(score);
    const descriptions = {
      specific: 'Very specific with clear actionable details',
      'mostly-specific': 'Mostly specific but could use some clarification',
      moderate: 'Moderately clear but lacks some specifics',
      vague: 'Quite vague and needs clarification',
      'very-vague': 'Extremely vague with no actionable information',
    };
    return descriptions[level];
  },

  generateCacheKey: (commentId: string, feedback: string): string => {
    // Create a simple hash from the feedback string
    let hash = 0;
    for (let i = 0; i < feedback.length; i++) {
      const char = feedback.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    const hashString = Math.abs(hash).toString(36).slice(0, 8);
    return `clarity_${commentId}_${hashString}`;
  },
};
